import { useNavigate } from 'react-router-dom'
import { Button, IconButton, DropdownItem } from '../common'

const SearchInput = ({ value, onChange, onClear }) => (
  <div className="relative max-w-md">
    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
      <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </div>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search employees, dept, role, location..."
      className="input-field pl-10 text-sm py-2 shadow-sm w-96"
    />
    {value && (
      <div className="absolute inset-y-0 right-0 pr-1 flex items-center">
        <IconButton onClick={onClear} size="sm">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </IconButton>
      </div>
    )}
  </div>
)

const TeamDropdown = ({ 
  dropdownRef, 
  onToggle, 
  isOpen, 
  selectedType, 
  onSelect 
}) => (
  <div className="relative" ref={dropdownRef}>
    <Button
      variant={selectedType ? 'primary' : 'outline'}
      onClick={onToggle}
      size="sm"
      className="w-34 justify-between"
    >
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <span>My Team</span>
      </div>
      <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    </Button>

    {isOpen && (
      <div className="absolute z-50 mt-1 w-36 bg-white border border-slate-300 rounded-lg shadow-lg py-1 overflow-hidden">
        <DropdownItem onClick={() => onSelect('direct-reports')} active={selectedType === 'direct-reports'} className="text-xs">
          Direct Reportings
        </DropdownItem>
        <DropdownItem onClick={() => onSelect('project-teams')} active={selectedType === 'project-teams'} className="text-xs">
          Project Teams
        </DropdownItem>
        {selectedType && (
          <DropdownItem onClick={() => onSelect(null)} className="text-xs text-slate-500 border-t border-slate-200">
            Clear Filter
          </DropdownItem>
        )}
      </div>
    )}
  </div>
)

const WorkforceActionBar = ({
  searchTerm,
  onSearchChange,
  onSearchClear,
  showMyTeamDropdown,
  onMyTeamDropdownToggle,
  selectedTeamType,
  onTeamTypeSelect,
  myTeamDropdownRef,
  showFilters,
  onFiltersToggle,
  activeFiltersCount,
  showAIFilter,
  onAIFilterToggle,
  aiFilteredEmployees,
  hasPermission,
  onBulkImportOpen,
  onExport,
  exportLoading
}) => {
  const navigate = useNavigate()
  const isFilterActive = showFilters || activeFiltersCount > 0

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
      <SearchInput value={searchTerm} onChange={onSearchChange} onClear={onSearchClear} />

      <div className="flex items-center gap-2">
        {!showFilters && !showAIFilter && !aiFilteredEmployees && (
          <TeamDropdown
            dropdownRef={myTeamDropdownRef}
            onToggle={onMyTeamDropdownToggle}
            isOpen={showMyTeamDropdown}
            selectedType={selectedTeamType}
            onSelect={onTeamTypeSelect}
          />
        )}

        {!showFilters && activeFiltersCount === 0 && (
          <Button
            variant={showAIFilter ? 'primary' : 'outline'}
            onClick={onAIFilterToggle}
            size="sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            AI Filter
          </Button>
        )}

        {!showAIFilter && !aiFilteredEmployees && (
          <Button onClick={onFiltersToggle} size="sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            <svg className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </Button>
        )}

        <div className="h-6 w-px bg-slate-200 mx-1" />

        {hasPermission('workforce-employees-add') && (
          <Button onClick={() => navigate('/employee/create')} size="sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Employee
          </Button>
        )}
        
        {hasPermission('workforce-import') && (
          <Button variant="outline" onClick={onBulkImportOpen} size="sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6" />
            </svg>
            Import
          </Button>
        )}
        
        {hasPermission('workforce-export') && (
          <Button
            variant="outline"
            onClick={onExport}
            loading={exportLoading}
            loadingText="Exporting..."
            size="sm"
          >
            {!exportLoading && (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            )}
            Export
          </Button>
        )}
      </div>
    </div>
  )
}

export default WorkforceActionBar
